/**
 * RTK Query API slice for Birthday Widget feature
 *
 * Provides API endpoints for managing birthday widget configuration
 * and fetching public birthday widget data.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';
import type {
  BirthdayWidgetConfig,
  BirthdayWidgetConfigUpdate,
  PublicBirthdayWidget,
} from '../types/birthdayWidget';

export const birthdayWidgetApi = createApi({
  reducerPath: 'birthdayWidgetApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['BirthdayWidgetConfig', 'PublicBirthdayWidget'],
  endpoints: builder => ({
    /**
     * Get or create birthday widget configuration for a studio
     *
     * GET /api/v1/studios/{studioId}/birthday-widget/config/
     */
    getBirthdayWidgetConfig: builder.query<BirthdayWidgetConfig, string>({
      query: studioId => `/studios/${studioId}/birthday-widget/config/`,
      providesTags: (result, error, studioId) => [
        { type: 'BirthdayWidgetConfig', id: studioId },
      ],
    }),

    /**
     * Update birthday widget configuration
     *
     * PATCH /api/v1/studios/{studioId}/birthday-widget/config/
     */
    updateBirthdayWidgetConfig: builder.mutation<
      BirthdayWidgetConfig,
      { studioId: string; data: BirthdayWidgetConfigUpdate }
    >({
      query: ({ studioId, data }) => ({
        url: `/studios/${studioId}/birthday-widget/config/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { studioId }) => [
        { type: 'BirthdayWidgetConfig', id: studioId },
        // Also invalidate public widget when config changes
        { type: 'PublicBirthdayWidget', id: studioId },
      ],
    }),

    /**
     * Get public birthday widget data (no authentication required)
     *
     * GET /api/v1/public/birthday-widget/{studioSlugOrId}/
     */
    getPublicBirthdayWidget: builder.query<PublicBirthdayWidget, string>({
      query: studioIdentifier => `/public/birthday-widget/${studioIdentifier}/`,
      providesTags: (result, error, studioIdentifier) => [
        { type: 'PublicBirthdayWidget', id: studioIdentifier },
      ],
    }),
  }),
});

export const {
  useGetBirthdayWidgetConfigQuery,
  useUpdateBirthdayWidgetConfigMutation,
  useGetPublicBirthdayWidgetQuery,
} = birthdayWidgetApi;
